// Thin typed fetch wrapper for the console JSON API. Same-origin `/api/*` —
// in dev, vite.config.ts proxies it to server.ts (or mock-server.ts); in
// prod, the frontend container's static server reverse-proxies it to
// 127.0.0.1:${CONSOLE_PORT}. Screen features add their own request/response
// types to lib/types.ts and their own methods here — this file is shared,
// so keep additions additive (new function, not edits to existing ones)
// to avoid merge conflicts between parallel feature branches.

import type {
  AccountFilterState,
  CampaignDetailResponse,
  CampaignFirstTouchEnabledResponse,
  CampaignObjectiveResponse,
  CampaignsListResponse,
  ConversationDetail,
  DecideIntent,
  InboxData,
  Lead,
  LeadFieldWriteResult,
  LiveState,
  MeResponse,
} from "./types";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/v2${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    // server.ts answers an unauthenticated /api/v2/* fetch with a plain 401
    // (not its usual 303 to /login) precisely so this branch can act on it —
    // a redirect would just get silently auto-followed by fetch() into
    // CONSOLE_AUTH_MODE=workos's /login -> AuthKit chain and die as an opaque
    // CORS failure, leaving the SPA shell rendered with no data and no
    // indication the visitor was never logged in. Force a real top-level
    // navigation instead of returning to the caller.
    if (res.status === 401) {
      window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return new Promise<T>(() => {}); // navigating away; never resolve
    }
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // Shared
  getMe: () => request<MeResponse>("/me"),
  getAccountFilter: () => request<AccountFilterState>("/account-filter"),
  setAccountFilter: (accountId: string | null) =>
    request<AccountFilterState>("/account-filter", {
      method: "POST",
      body: JSON.stringify({ accountId }),
    }),

  // Leads/All (feature/leads-all owns the route shape on the server side).
  // The three setters respond {ok, message} (LeadFieldWriteResult) — same
  // shape the equivalent form-POST routes already return for a JSON request —
  // not the updated Lead itself, so the leads-all screen updates its own
  // local row state optimistically rather than replacing it from the
  // response (see leads-all/page.tsx's own comment on this).
  // view=all: the server itself defaults ?view= to "positive" when absent
  // (services/v2/ui/server.ts's parseFilters) — this page's own tab filtering
  // (LeadsAllPage's viewFiltered) is client-side over the FULL dataset, so an
  // unfiltered fetch here was being silently pre-narrowed to positive-only
  // before the frontend ever saw it. Every tab except "Positive replies" then
  // showed exactly the same (wrong, narrowed) rows. Fetch everything once;
  // let the client-side tabs do the actual filtering they were built to do.
  getLeads: () => request<Lead[]>("/leads?view=all"),
  setMeetingStatus: (leadId: string, meetingStatus: string | null) =>
    request<LeadFieldWriteResult>(`/leads/${leadId}/meeting-status`, {
      method: "POST",
      body: JSON.stringify({ meetingStatus }),
    }),
  setFollowUp: (leadId: string, followUp: string | null) =>
    request<LeadFieldWriteResult>(`/leads/${leadId}/follow-up`, {
      method: "POST",
      body: JSON.stringify({ followUp }),
    }),
  setMeetingDate: (leadId: string, meetingDate: string | null) =>
    request<LeadFieldWriteResult>(`/leads/${leadId}/meeting-date`, {
      method: "POST",
      body: JSON.stringify({ meetingDate }),
    }),

  // Inbox / Conversation Detail (feature/inbox, feature/conversation-detail)
  // `filters` mirrors services/v2/ui/server.ts's parseFilters() query params
  // (class/campaign/q/handover/state/priority/sort/...) — the Inbox screen
  // itself only exposes the priority 2-way toggle today (the rest of the
  // filter form was removed on operator instruction, see views.ts's own
  // comment on inboxScreen()), but the route accepts the full set.
  getInbox: (filters: Record<string, string> = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request<InboxData>(`/inbox${qs ? `?${qs}` : ""}`);
  },
  getConversation: (id: string) => request<ConversationDetail>(`/c/${id}`),
  dismissConversation: (id: string, approver?: string) =>
    request<{ ok: boolean; message: string }>(`/c/${id}/dismiss`, { method: "POST", body: JSON.stringify({ approver }) }),
  undismissConversation: (id: string) => request<{ ok: boolean; message: string }>(`/c/${id}/undismiss`, { method: "POST" }),
  decideConversation: (id: string, body: { intent: DecideIntent; aiReplyId: string; text: string; approver: string }) =>
    request<{ ok: boolean; message: string }>(`/c/${id}/decide`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  composeSend: (id: string, body: { text: string; approver: string }) =>
    request<{ ok: boolean; message: string }>(`/c/${id}/compose-send`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Response shape mirrors services/v2/ui/server.ts's POST /api/v2/c/:id/status
  // exactly (see the HTML route's relayReclassify()-branch comments for what
  // each `outcome` value means) — richer than a plain {ok,message}, since the
  // Inbox/Conversation screens surface HubSpot sync results and "someone else
  // changed it first" (won:false) distinctly.
  setStatus: (id: string, choice: "positive" | "neutral" | "negative", opts?: { approver?: string; createIfMissing?: boolean }) =>
    request<{
      ok: boolean;
      message: string;
      outcome?: "still-sending" | "noop-already" | "failed" | "applied";
      label?: string;
      won?: boolean;
      currentLabel?: string | null;
      hubspot?: "created" | "updated" | "none";
    }>(`/c/${id}/status`, {
      method: "POST",
      body: JSON.stringify({ choice, ...opts }),
    }),
  // Research rail "Refresh research" control (research-panel.tsx). Can take a
  // while server-side (refreshProspectResearch's own Iris round-trip retry
  // budget) — the request has no client-side timeout, same as every other
  // method here, so this simply resolves whenever the server responds.
  refreshResearch: (id: string, focusPrompt?: string) =>
    request<{
      ok: boolean;
      message: string;
      outcome?: "still-sending" | "failed" | "research-failed" | "applied";
      redraftStarted?: boolean;
    }>(`/c/${id}/refresh-research`, {
      method: "POST",
      body: JSON.stringify({ focusPrompt }),
    }),
  // GET /api/v2/c/:id/decided — vestigial for a JS client (decide/compose-send
  // above already resolve synchronously) but implemented server-side for
  // parity; kept here in case a caller wants the same confirmation data
  // decide/compose-send's `message` already carries.
  getDecided: (id: string, params: { ok?: string; error?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]).toString();
    return request<{ leadName: string | null; kind: "ok" | "error"; message: string }>(`/c/${id}/decided${qs ? `?${qs}` : ""}`);
  },
  // /live.json already returns JSON as-is (services/v2/ui/server.ts) and isn't
  // under the /api/v2 prefix — fetched directly rather than duplicated as a
  // /api/v2/live.json route. Same cadence LIVE_SCRIPT's polling fallback uses
  // (services/v2/ui/styles.ts's CLIENT_POLL_MS, 2x the server's SSE tick,
  // default 10s) — see the Inbox page's own poll interval.
  getLive: async (): Promise<LiveState> => {
    const res = await fetch("/live.json", { credentials: "include", headers: { accept: "application/json" } });
    // Same reasoning as request()'s 401 branch above — getLive() bypasses
    // that wrapper entirely (/live.json isn't under /api/v2), so it needs
    // its own copy of the redirect-to-login-on-401 behavior or a session
    // expiring mid-poll would just fail silently forever instead of ever
    // sending the visitor back to /login.
    if (res.status === 401) {
      window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return new Promise<LiveState>(() => {});
    }
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => res.statusText));
    return (await res.json()) as LiveState;
  },

  // Campaigns (feature/campaigns) — GET /api/v2/campaigns, GET
  // /api/v2/campaigns/:id, POST /api/v2/campaigns/:id/objective,
  // POST /api/v2/campaigns/:id/first-touch-enabled
  // (services/v2/ui/server.ts's buildCampaignsListData()/buildCampaignDetailData()).
  getCampaigns: () => request<CampaignsListResponse>("/campaigns"),
  getCampaign: (id: string) => request<CampaignDetailResponse>(`/campaigns/${id}`),
  setCampaignObjective: (id: string, objective: string) =>
    request<CampaignObjectiveResponse>(`/campaigns/${id}/objective`, {
      method: "POST",
      body: JSON.stringify({ objective }),
    }),
  setCampaignFirstTouchEnabled: (id: string, enabled: boolean) =>
    request<CampaignFirstTouchEnabledResponse>(`/campaigns/${id}/first-touch-enabled`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),
};
